import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { useDossierPlanning } from './shared/DossierPlanification';
import './VehiculeModal.css';

// ─── Fiche véhicule (champ Rechercher du menu) ───────────────────────────────
// Identifier le véhicule et savoir où le retrouver. Lecture seule : chaque
// tuile mène à son module, la carte atelier ouvre la vraie modale du dossier
// Planning. Les modules invisibles pour l'utilisateur (droits ou plaque)
// arrivent à `null` et ne sont cités nulle part, pas même dans « Absent de ».

const LIVRAISON = {
  livre: 'Reçue',
  en_commande: 'Commandée',
  recu_partiellement: 'Reçue en partie',
  livre_partiellement: 'Reçue en partie',
};

const jourIso = (v) => String(v || '').slice(0, 10);
const fmt = (v) => {
  if (!v) return '—';
  const d = new Date(String(v).length === 10 ? `${v}T12:00:00` : v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR');
};
const fmtCourt = (v) => {
  if (!v) return '';
  const d = new Date(String(v).length === 10 ? `${v}T12:00:00` : v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
};
const fmtJourHeure = (date, heure) => {
  if (!date) return null;
  const d = new Date(`${jourIso(date)}T12:00:00`);
  const j = d.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' });
  return heure ? `${j} · ${String(heure).slice(0, 5)}` : j;
};
const euros = (v) => (v == null ? null
  : `${Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);
const pluriel = (n, mot) => `${n} ${mot}${n > 1 ? 's' : ''}`;

function depuis(iso) {
  if (!iso) return null;
  const j = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (j <= 0) return "depuis aujourd'hui";
  return j === 1 ? 'depuis hier' : `depuis ${j} j`;
}

// Tuiles « Où trouver ce véhicule », dans l'ordre d'usage au comptoir.
function construireTuiles(d, enCours) {
  const m = d.modules;
  const today = new Date().toISOString().slice(0, 10);
  const t = [];
  const add = (cle, icone, nom, rows, resume, extra = {}) => t.push({ cle, icone, nom, rows, resume, ...extra });

  if (m.planning !== null) {
    const p = enCours || m.planning[0];
    add('planning', '📅', 'Planning atelier', m.planning,
      !p ? null
        : enCours
          ? [p.numero_or ? `OR ${p.numero_or}` : fmt(p.date_intervention), p.statut_atelier_nom].filter(Boolean).join(' · ')
          : `${pluriel(m.planning.length, 'passage')} · dernier ${fmt(p.date_intervention)}`,
      { interventionId: p?.id });
  }
  if (m.pieces !== null) {
    const c = m.pieces.find(x => x.num_or === d.or_courant) || m.pieces[0];
    add('pieces-atelier', '📦', 'Pièces', m.pieces,
      c ? [`OR ${c.num_or}`, LIVRAISON[c.statut_livraison]?.toLowerCase(),
        m.pieces.length > 1 ? pluriel(m.pieces.length, 'commande') : null].filter(Boolean).join(' · ') : null);
  }
  if (m.ors !== null) {
    const total = m.ors.reduce((s, o) => s + (Number(o.montant_ht) || 0), 0);
    add('or-en-cours', '🔩', 'OR en cours', m.ors,
      m.ors.length ? `${pluriel(m.ors.length, 'OR ouvert')}${total ? ` · ${euros(total)} HT` : ''}` : null);
  }
  if (m.parking !== null) {
    const p = m.parking[0];
    add('parking', '🅿️', 'Gestion Parking', m.parking,
      p ? [p.allee ? `Allée ${p.allee}` : 'Sur le parking', p.updated_at ? `relevé le ${fmtCourt(p.updated_at)}` : null].filter(Boolean).join(' · ') : null);
  }
  if (m.litiges !== null) {
    const ouverts = m.litiges.filter(l => !l.archive_le && !l.date_cloture);
    const retard = ouverts.some(l => l.date_prochaine_action && jourIso(l.date_prochaine_action) <= today);
    const prochain = ouverts.map(l => jourIso(l.date_prochaine_action)).filter(Boolean).sort()[0];
    add('litiges', '⚖️', 'Litiges', m.litiges,
      !m.litiges.length ? null
        : ouverts.length
          ? `${pluriel(ouverts.length, 'ouvert')}${prochain ? ` · action le ${fmtCourt(prochain)}` : ''}`
          : `${pluriel(m.litiges.length, 'litige')} archivé${m.litiges.length > 1 ? 's' : ''}`,
      { alerte: ouverts.length > 0, retard });
  }
  if (m.vip !== null) {
    const v = m.vip[0];
    add('reception-vip', '🏁', 'Réception VIP', m.vip,
      v ? [v.statut, `reçu le ${fmtCourt(v.created_at)}`].filter(Boolean).join(' · ') : null);
  }
  if (m.demandes !== null) {
    const ouvertes = m.demandes.filter(x => x.statut !== 'traite' && x.statut !== 'archive').length;
    add('leads-services', '💬', 'Demandes clients', m.demandes,
      m.demandes.length ? `${pluriel(m.demandes.length, 'demande')}${ouvertes ? ` · ${ouvertes} en cours` : ''}` : null);
  }
  if (m.takata !== null) {
    const k = m.takata[0];
    add('takata', '🎈', 'Takata', m.takata, k ? [k.statut, k.recall_code].filter(Boolean).join(' · ') : null);
  }
  if (m.garantie !== null) {
    const g = m.garantie[0];
    add('garantie', '📋', 'Garantie', m.garantie,
      g ? `${pluriel(m.garantie.length, 'dossier')} · dernier ${fmt(g.date_garantie)}` : null);
  }
  return t;
}

export default function VehiculeModal({ immat, user, editMode, onClose, onNavigate }) {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pieces, setPieces] = useState(null);   // lignes du carnet de l'OR courant
  const [photos, setPhotos] = useState([]);
  const { ouvrir: ouvrirDossier, modale: modaleDossier, chargement } = useDossierPlanning({ user, editMode });

  const cle = immat.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true); setErreur(null); setData(null);
    api.get(`/vehicule/${encodeURIComponent(cle)}`, { signal: ctrl.signal })
      .then(r => setData(r.data))
      .catch(e => {
        if (e?.name === 'CanceledError') return;
        setErreur(e?.response?.data?.error || 'Impossible de charger la fiche.');
      })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [cle]);

  const enCours = data?.en_cours || null;
  const orCourant = data?.or_courant || null;

  // Pièces de l'OR courant : même route que le dossier Planning (droits et
  // périmètre appliqués côté serveur ; un refus laisse simplement la carte vide).
  useEffect(() => {
    setPieces(null);
    if (!orCourant || (data.modules.pieces === null && data.modules.planning === null)) return;
    const ctrl = new AbortController();
    api.get(`/carnet-commande/by-or/${encodeURIComponent(orCourant)}`, { signal: ctrl.signal })
      .then(r => setPieces(r.data?.found ? (r.data.lignes || []) : []))
      .catch(() => setPieces([]));
    return () => ctrl.abort();
  }, [orCourant, data]);

  useEffect(() => {
    setPhotos([]);
    const num = enCours?.numero_or;
    const nb = (data?.photos || []).find(p => p.numero_or === num)?.nb || 0;
    if (!num || !nb) return;
    const ctrl = new AbortController();
    api.get(`/atelier-photos/by-or/${encodeURIComponent(num)}`, { signal: ctrl.signal })
      .then(r => setPhotos(r.data?.photos || []))
      .catch(() => setPhotos([]));
    return () => ctrl.abort();
  }, [enCours, data]);

  const tuiles = useMemo(() => (data ? construireTuiles(data, enCours) : []), [data, enCours]);
  const presents = tuiles.filter(t => t.rows.length > 0);
  const absents = tuiles.filter(t => t.rows.length === 0);
  const orOuvert = data?.modules.ors?.find(o => o.num_or === orCourant) || null;

  const aller = (tuile) => {
    if (tuile.cle === 'planning' && tuile.interventionId) {
      ouvrirDossier({ planning_intervention_id: tuile.interventionId });
      return;
    }
    onNavigate(tuile.cle);
    onClose();
  };

  const v = data?.vehicule || {};
  const plaque = data?.immat || cle;
  const nbPhotos = (data?.photos || []).find(p => p.numero_or === enCours?.numero_or)?.nb || 0;
  const piecesRecues = (pieces || []).filter(p => p.statut_livraison === 'livre').length;

  // Pastilles d'état de l'en-tête : uniquement ce qui demande une action ou
  // situe le véhicule maintenant.
  const pastilles = [];
  if (enCours?.statut_atelier_nom) pastilles.push({ cls: 'warn', txt: `🔧 ${enCours.statut_atelier_nom}` });
  else if (enCours) pastilles.push({ cls: 'info', txt: `📅 Planifié le ${fmt(enCours.date_intervention)}` });
  if (data?.modules.parking?.length) pastilles.push({ cls: 'accent', txt: `🅿️ Sur le parking${data.modules.parking[0].allee ? ` · ${data.modules.parking[0].allee}` : ''}` });
  const tLitiges = tuiles.find(t => t.cle === 'litiges');
  if (tLitiges?.alerte) pastilles.push({ cls: 'error', txt: tLitiges.retard ? '⚖️ Litige · action en retard' : '⚖️ Litige ouvert' });

  return (
    <>
    <div className="modal-overlay">
      <div className="modal-content vf-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header vf-header">
          <div className="vf-ident">
            <span className="vf-plaque" aria-label="Immatriculation">{plaque}</span>
            <div className="vf-ident-txt">
              <h2>{[v.marque, v.modele].filter(Boolean).join(' ') || 'Véhicule'}</h2>
              <div className="vf-meta">
                {v.vin && <span>VIN <code>{v.vin}</code></span>}
                {v.kilometrage && <span>{Number(v.kilometrage).toLocaleString('fr-FR')} km</span>}
                {v.client && <span>{v.client}</span>}
                {v.concession && <span>{v.concession}</span>}
              </div>
              {pastilles.length > 0 && (
                <div className="vf-pastilles">
                  {pastilles.map(p => <span key={p.txt} className={`vf-pastille vf-${p.cls}`}>{p.txt}</span>)}
                </div>
              )}
            </div>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Fermer">✕</button>
        </div>

        <div className="modal-body vf-body">
          {loading && <div className="vf-vide">Chargement…</div>}
          {!loading && erreur && <div className="vf-vide">{erreur}</div>}
          {!loading && data && presents.length === 0 && (
            <div className="vf-vide">
              Aucun dossier pour <b>{cle}</b> dans votre périmètre.
              <div className="vf-hint">La recherche accepte une immatriculation, un n° d'OR ou un VIN, et suit la concession choisie dans le menu.</div>
            </div>
          )}

          {!loading && data && presents.length > 0 && (
            <>
              {data.type !== 'immat' && (
                <div className="vf-hint">
                  {data.type === 'or' ? `Véhicule retrouvé par le n° d'OR ${data.or_recherche}.` : 'Véhicule retrouvé par son VIN.'}
                </div>
              )}

              <section className="vf-carte">
                <div className="vf-carte-titre">Où trouver ce véhicule · {pluriel(presents.length, 'module')}</div>
                <div className="vf-tuiles">
                  {presents.map(t => (
                    <button key={t.cle} type="button"
                      className={`vf-tuile${t.alerte ? ' vf-tuile--alerte' : ''}`}
                      onClick={() => aller(t)}
                      disabled={t.cle === 'planning' && chargement != null}
                      title={t.cle === 'planning' ? 'Ouvrir le dossier' : `Aller à ${t.nom}`}>
                      <span className="vf-tuile-icone" aria-hidden="true">{t.icone}</span>
                      <span className="vf-tuile-txt">
                        <b>{t.nom}</b>
                        <span>{t.resume}</span>
                      </span>
                      <span className="vf-tuile-fleche" aria-hidden="true">→</span>
                    </button>
                  ))}
                </div>
                {absents.length > 0 && (
                  <div className="vf-absents">Absent de : {absents.map(t => t.nom).join(', ')}.</div>
                )}
              </section>

              {(enCours || orOuvert || (pieces && pieces.length > 0)) && (
                <div className="vf-grille">
                  {enCours && (
                    <section className="vf-carte">
                      <div className="vf-carte-entete">
                        <span className="vf-carte-titre">
                          {jourIso(enCours.date_intervention) > new Date().toISOString().slice(0, 10) ? 'Rendez-vous atelier' : 'Dossier atelier en cours'}
                        </span>
                        <button type="button" className="vf-lien" disabled={chargement != null}
                          onClick={() => ouvrirDossier({ planning_intervention_id: enCours.id })}>
                          {chargement ? 'Ouverture…' : 'Ouvrir le dossier →'}
                        </button>
                      </div>
                      <div className="vf-dossier-ligne">
                        <span>
                          {enCours.numero_or ? <>OR <code>{enCours.numero_or}</code></> : 'Sans n° d’OR'}
                          {enCours.type_intervention ? ` · ${enCours.type_intervention}` : ''}
                        </span>
                        {enCours.statut_atelier_nom && (
                          <span className="vf-statut"
                            style={enCours.statut_atelier_couleur ? { background: enCours.statut_atelier_couleur, color: enCours.statut_atelier_text_color || undefined } : undefined}>
                            {enCours.statut_atelier_nom}
                            {enCours.statut_atelier_since && <small> · {depuis(enCours.statut_atelier_since)}{enCours.statut_atelier_since_estime ? ' (estimé)' : ''}</small>}
                          </span>
                        )}
                      </div>
                      <dl className="vf-kv">
                        <dt>Planifié</dt><dd>{fmtJourHeure(enCours.date_intervention, enCours.heure_debut)}{enCours.duree ? ` · ${String(enCours.duree).replace('.', ',')} h` : ''}</dd>
                        {(enCours.date_reception || enCours.heure_reception) && (
                          <><dt>Réception</dt><dd>{fmtJourHeure(enCours.date_reception || enCours.date_intervention, enCours.heure_reception)}{enCours.numero_cravate ? ` · cravate ${enCours.numero_cravate}` : ''}</dd></>
                        )}
                        {enCours.mecanicien_nom && <><dt>Mécanicien</dt><dd>{enCours.mecanicien_nom}</dd></>}
                        {enCours.date_restitution_prevue && (
                          <><dt>Restitution prévue</dt><dd>{fmtJourHeure(enCours.date_restitution_prevue, enCours.heure_restitution_prevue)}</dd></>
                        )}
                        {enCours.client_attend && <><dt>Client</dt><dd>attend sur place</dd></>}
                      </dl>
                      {nbPhotos > 0 && (
                        <div className="vf-photos">
                          <span className="vf-carte-titre">Photos · {nbPhotos}</span>
                          <div className="vf-vignettes">
                            {photos.slice(0, 4).map(p => (
                              (p.content_type || '').startsWith('video/')
                                ? <span key={p.id} className="vf-vignette vf-vignette--video">▶</span>
                                : <img key={p.id} className="vf-vignette" src={p.url} alt="" loading="lazy" />
                            ))}
                            {nbPhotos > 4 && <span className="vf-vignette vf-vignette--plus">+{nbPhotos - 4}</span>}
                          </div>
                        </div>
                      )}
                    </section>
                  )}

                  {(orOuvert || (pieces && pieces.length > 0)) && (
                    <div className="vf-colonne">
                      {pieces && pieces.length > 0 && (
                        <section className="vf-carte">
                          <div className="vf-carte-entete">
                            <span className="vf-carte-titre">Pièces de l'OR {orCourant}</span>
                            {data.modules.pieces !== null && (
                              <button type="button" className="vf-lien" onClick={() => { onNavigate('pieces-atelier'); onClose(); }}>Suivi Pièces →</button>
                            )}
                          </div>
                          <div className="vf-avancement">
                            <span>{piecesRecues} sur {pluriel(pieces.length, 'ligne')} reçue{piecesRecues > 1 ? 's' : ''}</span>
                            <div className="vf-barre"><span style={{ width: `${Math.round((piecesRecues / pieces.length) * 100)}%` }} /></div>
                          </div>
                          <div className="vf-pieces">
                            {pieces.slice(0, 6).map((p, k) => (
                              <div className="vf-piece" key={`${p.reference}-${k}`}>
                                <div>
                                  <span>{p.description || 'Pièce'}</span>
                                  <code>{p.reference}</code>
                                </div>
                                <span className={`vf-pastille ${p.statut_livraison === 'livre' ? 'vf-success' : p.anomalie_active ? 'vf-error' : 'vf-warn'}`}>
                                  {p.anomalie_active ? 'Anomalie'
                                    : `${LIVRAISON[p.statut_livraison] || 'Commandée'}${p.statut_livraison !== 'livre' && p.sifa?.date_eta ? ` · arrivée ${fmtCourt(p.sifa.date_eta)}` : ''}`}
                                </span>
                              </div>
                            ))}
                            {pieces.length > 6 && <div className="vf-absents">+ {pluriel(pieces.length - 6, 'autre ligne')}</div>}
                          </div>
                        </section>
                      )}
                      {orOuvert && (
                        <section className="vf-carte">
                          <div className="vf-carte-titre">OR ouvert (DMS)</div>
                          <dl className="vf-kv">
                            <dt>Ouvert le</dt><dd>{fmt(orOuvert.date_creation)}</dd>
                            {orOuvert.montant_ht != null && <><dt>Montant HT</dt><dd>{euros(orOuvert.montant_ht)}</dd></>}
                            {orOuvert.receptionnaire && <><dt>Réceptionnaire</dt><dd>{orOuvert.receptionnaire}</dd></>}
                            {orOuvert.action && <><dt>Action</dt><dd>{orOuvert.action}</dd></>}
                          </dl>
                        </section>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Fermer</button>
          {enCours && (
            <button className="btn-primary" disabled={chargement != null}
              onClick={() => ouvrirDossier({ planning_intervention_id: enCours.id })}>
              Ouvrir le dossier atelier
            </button>
          )}
        </div>
      </div>
    </div>
    {modaleDossier}
    </>
  );
}
